import { useState, useEffect, useRef } from "react";
import { TrendingUp, Wallet, Receipt, Home, Percent, Info } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import ToolShell from "./ToolShell";
import ToolExportButton from "./ToolExportButton";
import { cities, estimateNightly, estimateOccupancy, fmt } from "./calculatorData";

const inputStyle = { border: "1.5px solid #E5E5E5", borderRadius: 10, padding: "11px 14px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#1A1A1A", background: "#fff", width: "100%", boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" };
const labelStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#717171", display: "block", marginBottom: 6 };
const cardStyle = { background: "#fff", border: "1px solid #EBEBEB", borderRadius: 18, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Seasonal demand multipliers (avg = 1.0) for the 12-month projection
const SEASON = [0.78, 0.86, 0.93, 1.02, 1.08, 1.15, 1.22, 1.22, 1.15, 1.08, 0.95, 0.86];

const DEFAULTS = {
  utilities: 180, councilTax: 150, internet: 35, insurance: 25, mortgage: 0,
  avgStay: 3, cleaning: 45, otherPct: 2,
  platformPct: 16, managementPct: 18, vatPct: 20, paymentPct: 0,
};

const FAQS = [
  { q: "How accurate is the Airbnb deal analyser?", a: "The analyser uses real performance data from 50+ Truestays-managed properties across 16 UK cities, blended with platform fee and occupancy benchmarks. Your actual income will vary with property condition, presentation, dynamic pricing and seasonal demand — use the figures as a realistic planning baseline, then request a personalised report for a property-specific estimate." },
  { q: "What costs are included?", a: "Fixed monthly costs (utilities, council tax, internet, insurance and mortgage), variable costs (cleaning per changeover based on average length of stay, plus a percentage for sundries), platform/OTA commissions, the management fee with VAT, and payment processing fees. Every line is editable so you can match your real situation." },
  { q: "How is net profit calculated?", a: "Monthly gross revenue is nightly rate × occupancy × 30 nights. We then subtract total fixed costs, variable costs (changeovers = booked nights ÷ average stay length × cleaning fee, plus sundries), and all commissions and fees. The monthly net is multiplied by 12 for the annual figure." },
  { q: "Can I export the results?", a: "Yes — click 'Export PDF' to download a clean, branded summary of your analysis including the revenue split, 12-month projection, full cost breakdown and profit sensitivity matrix. No email required." },
  { q: "Is short-letting more profitable than a long let?", a: "For most UK properties in our markets, short-lets net 40–80% more than traditional ast rental income, even after all costs and fees. Use the analyser to see your numbers, then compare directly with our long-let vs short-let calculator." },
];

function Field({ label, value, onChange, prefix, suffix, type = "number", min = 0 }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: "relative" }}>
        {prefix && <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#999", pointerEvents: "none" }}>{prefix}</span>}
        <input type={type} min={min} value={value} onChange={onChange} style={{ ...inputStyle, paddingLeft: prefix ? 28 : 14, paddingRight: suffix ? 30 : 14 }} onFocus={e => e.target.style.borderColor = "#4A6DC7"} onBlur={e => e.target.style.borderColor = "#E5E5E5"} />
        {suffix && <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#999", pointerEvents: "none" }}>{suffix}</span>}
      </div>
    </div>
  );
}

function SectionCard({ icon: Icon, title, children }) {
  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16 }}>
        <Icon size={17} color="#4A6DC7" />
        <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div style={{ background: "#F7F7F7", borderRadius: 14, padding: "16px 18px", border: "1px solid #F0F0F0" }}>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#717171", margin: "0 0 6px" }}>{label}</p>
      <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 24, color: accent || "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{value}</p>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#666" }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: "inline-block" }} /> {label}
    </span>
  );
}

export default function AirbnbIncomeAnalyser() {
  const [city, setCity] = useState("Manchester");
  const [beds, setBeds] = useState(2);
  const [guests, setGuests] = useState(4);
  const [nightly, setNightly] = useState(0);
  const [occupancy, setOccupancy] = useState(0);
  const [costs, setCosts] = useState(DEFAULTS);
  const reportRef = useRef(null);

  // Auto-fill nightly & occupancy from city averages whenever the property selectors change
  useEffect(() => {
    if (city && !cities.includes(city)) return;
    const label = ["Studio", "1 bed", "2 beds", "3 beds", "4+ beds"][Math.min(Math.max(beds, 0), 4)];
    setNightly(estimateNightly(city, label, "Apartment"));
    setOccupancy(Math.round(estimateOccupancy(city) * 100));
  }, [city, beds]);

  const setCost = (f) => (e) => setCosts(p => ({ ...p, [f]: parseFloat(e.target.value) || 0 }));

  const occ = occupancy / 100;
  const monthlyGross = nightly * occ * 30;
  const bookedNights = occ * 30;
  const changeovers = costs.avgStay > 0 ? bookedNights / costs.avgStay : 0;
  const cleaningCost = changeovers * costs.cleaning;
  const otherVariable = monthlyGross * (costs.otherPct / 100);
  const totalVariable = cleaningCost + otherVariable;
  const totalFixed = costs.utilities + costs.councilTax + costs.internet + costs.insurance + costs.mortgage;
  const platformFee = monthlyGross * (costs.platformPct / 100);
  const mgmtFee = monthlyGross * (costs.managementPct / 100);
  const vatOnMgmt = mgmtFee * (costs.vatPct / 100);
  const paymentFee = monthlyGross * (costs.paymentPct / 100);
  const totalFees = platformFee + mgmtFee + vatOnMgmt + paymentFee;
  const totalCosts = totalFixed + totalVariable + totalFees;
  const monthlyNet = monthlyGross - totalCosts;
  const annualNet = monthlyNet * 12;
  const netMargin = monthlyGross > 0 ? (monthlyNet / monthlyGross) * 100 : 0;

  // 12-month projection (seasonal scaling of net)
  const monthlyProjection = SEASON.map((m, i) => ({ name: MONTHS[i], net: Math.round(monthlyNet * m) }));

  // Sensitivity matrix: occupancy rows × nightly rate columns
  const occBase = Math.round(occupancy / 10) * 10;
  const occRows = [occBase - 20, occBase - 10, occBase, occBase + 10, occBase + 20].map(v => Math.max(40, Math.min(100, v)));
  const rateBase = nightly;
  const rateCols = [rateBase - 40, rateBase - 20, rateBase, rateBase + 20, rateBase + 40].map(v => Math.max(20, v));
  const netAt = (rate, occPct) => {
    const g = rate * (occPct / 100) * 30;
    const bn = (occPct / 100) * 30;
    const co = costs.avgStay > 0 ? bn / costs.avgStay : 0;
    const tv = co * costs.cleaning + g * (costs.otherPct / 100);
    const tf = totalFixed;
    const fees = g * ((costs.platformPct + costs.managementPct * (1 + costs.vatPct / 100) + costs.paymentPct) / 100);
    return g - tv - tf - fees;
  };

  const splitSegments = [
    { label: "Net profit", value: Math.max(monthlyNet, 0), color: "#4A6DC7" },
    { label: "Fees & commission", value: totalFees, color: "#1A1A1A" },
    { label: "Variable costs", value: totalVariable, color: "#9A9A9A" },
    { label: "Fixed costs", value: totalFixed, color: "#D5D5D5" },
  ].filter(s => s.value > 0);
  const splitTotal = splitSegments.reduce((a, s) => a + s.value, 0) || 1;

  const summary = `Airbnb Deal Analysis — ${city}, ${beds} beds · ${guests} guests. Nightly: £${nightly}. Occupancy: ${occupancy}%. Monthly gross: ${fmt(monthlyGross)}. Fixed costs: ${fmt(totalFixed)}. Variable costs: ${fmt(totalVariable)}. Fees: ${fmt(totalFees)}. Monthly net: ${fmt(monthlyNet)}. Annual net: ${fmt(annualNet)}. Net margin: ${netMargin.toFixed(0)}%.`;

  // Inject FAQ structured data for SEO
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "analyser-faq-schema";
    script.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQS.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
    document.head.appendChild(script);
    return () => { const el = document.getElementById("analyser-faq-schema"); if (el) el.remove(); };
  }, []);

  return (
    <ToolShell
      title="Airbnb Deal Analyser"
      tagline="A full short-let deal analyser — revenue, every cost, fees and a 12-month projection. Free, instant, exportable."
      description="Free Airbnb deal analyser. Estimate short-let revenue, operating costs, fees and net profit for any UK property. Includes 12-month projection, profit sensitivity matrix and PDF export."
      slug="airbnb-deal-analyser"
    >
      <div className="analyser-grid" style={{ display: "grid", gridTemplateColumns: "390px 1fr", gap: 32, alignItems: "start" }}>
        {/* INPUTS */}
        <div>
          <SectionCard icon={Home} title="Your property">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="City" type="text" value={city} onChange={e => setCity(e.target.value)} />
              <Field label="Bedrooms" type="number" min={0} value={beds} onChange={e => setBeds(parseInt(e.target.value) || 0)} />
              <Field label="Guests" type="number" min={1} value={guests} onChange={e => setGuests(parseInt(e.target.value) || 1)} />
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#999", margin: "2px 0 0", lineHeight: 1.5 }}>Typing a known city pre-fills typical nightly rate & occupancy from our managed portfolio. Adjust anything below.</p>
            </div>
          </SectionCard>

          <SectionCard icon={TrendingUp} title="Revenue">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="Nightly rate" prefix="£" value={nightly} onChange={e => setNightly(parseFloat(e.target.value) || 0)} />
              <Field label="Occupancy rate" suffix="%" value={occupancy} onChange={e => setOccupancy(parseFloat(e.target.value) || 0)} />
            </div>
          </SectionCard>

          <SectionCard icon={Receipt} title="Fixed monthly costs">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="Utilities (gas, electric, water)" prefix="£" value={costs.utilities} onChange={setCost("utilities")} />
              <Field label="Council tax" prefix="£" value={costs.councilTax} onChange={setCost("councilTax")} />
              <Field label="Internet" prefix="£" value={costs.internet} onChange={setCost("internet")} />
              <Field label="Insurance" prefix="£" value={costs.insurance} onChange={setCost("insurance")} />
              <Field label="Mortgage / finance" prefix="£" value={costs.mortgage} onChange={setCost("mortgage")} />
            </div>
          </SectionCard>

          <SectionCard icon={Wallet} title="Variable costs">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="Average length of stay" suffix="nights" value={costs.avgStay} onChange={setCost("avgStay")} />
              <Field label="Cleaning per changeover" prefix="£" value={costs.cleaning} onChange={setCost("cleaning")} />
              <Field label="Other expenses" suffix="%" value={costs.otherPct} onChange={setCost("otherPct")} />
            </div>
          </SectionCard>

          <SectionCard icon={Percent} title="Fees & commission">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="Platform / OTA commission" suffix="%" value={costs.platformPct} onChange={setCost("platformPct")} />
              <Field label="Management fee" suffix="%" value={costs.managementPct} onChange={setCost("managementPct")} />
              <Field label="VAT on management fee" suffix="%" value={costs.vatPct} onChange={setCost("vatPct")} />
              <Field label="Payment processing" suffix="%" value={costs.paymentPct} onChange={setCost("paymentPct")} />
            </div>
          </SectionCard>
        </div>

        {/* RESULTS */}
        <div>
          <ToolExportButton slug="airbnb-deal-analyser" title="Airbnb Deal Analysis" targetRef={reportRef} fileName={`Airbnb-Deal-Analysis-${city}`} />

          <div ref={reportRef} style={{ background: "#fff", padding: 24, borderRadius: 16, border: "1px solid #EBEBEB" }}>
            {/* Report header (visible in PDF) */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0 16px", borderBottom: "1px solid #F0F0F0", marginBottom: 18 }}>
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 17, color: "#1A1A1A", margin: 0 }}>Airbnb Deal Analysis — {city}</p>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#888", margin: "4px 0 0" }}>{beds} beds · {guests} guests</p>
              </div>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/24682ceeb_TruestaysLogo.png" alt="Truestays" style={{ height: 30, width: "auto" }} />
            </div>

            {/* Metric cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }} className="analyser-metrics">
              <Metric label="Monthly gross" value={fmt(monthlyGross)} />
              <Metric label="Monthly net" value={fmt(monthlyNet)} accent="#4A6DC7" />
              <Metric label="Annual net" value={fmt(annualNet)} accent="#4A6DC7" />
              <Metric label="Net margin" value={`${netMargin.toFixed(0)}%`} />
            </div>

            {/* Revenue split */}
            <div style={{ ...cardStyle, marginBottom: 20 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", margin: "0 0 14px" }}>Where your monthly revenue goes</p>
              <div style={{ display: "flex", height: 30, borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                {splitSegments.map((s, i) => (
                  <div key={i} style={{ width: `${(s.value / splitTotal) * 100}%`, background: s.color, transition: "width 0.3s" }} />
                ))}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
                {splitSegments.map((s, i) => (
                  <div key={i} style={{ display: "flex", flexDirection: "column" }}>
                    <LegendDot color={s.color} label={s.label} />
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: "#1A1A1A", marginTop: 2 }}>{fmt(s.value)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 12-month projection chart */}
            <div style={{ ...cardStyle, marginBottom: 20 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", margin: "0 0 4px" }}>12-month net profit projection</p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#999", margin: "0 0 14px" }}>Seasonal demand applied to your average monthly net.</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthlyProjection} margin={{ top: 4, right: 8, left: -8, bottom: 0 }} barSize={26}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: "#888" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "'DM Sans', sans-serif", fill: "#888" }} axisLine={false} tickLine={false} tickFormatter={v => `£${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt(v)} contentStyle={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, borderRadius: 8, border: "1px solid #E5E5E5" }} cursor={{ fill: "#FAFAFA" }} />
                  <Bar dataKey="net" fill="#4A6DC7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Cost breakdown table */}
            <div style={{ ...cardStyle, marginBottom: 20 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", margin: "0 0 12px" }}>Monthly cost breakdown</p>
              {[
                ["Gross revenue", fmt(monthlyGross), false],
                ["Fixed costs", `– ${fmt(totalFixed)}`, true],
                [`  Utilities, council tax, internet, insurance, mortgage`, "", true],
                ["Variable costs", `– ${fmt(totalVariable)}`, true],
                [`  ${Math.round(changeovers)} changeovers × ${fmt(costs.cleaning)} cleaning + sundries`, "", true],
                ["Platform / OTA commission", `– ${fmt(platformFee)}`, true],
                [`Management fee (${costs.managementPct}%)`, `– ${fmt(mgmtFee)}`, true],
                [`VAT on management fee`, `– ${fmt(vatOnMgmt)}`, true],
                ["Payment processing", `– ${fmt(paymentFee)}`, true],
                ["Monthly net profit", fmt(monthlyNet), false],
                ["Annual net profit", fmt(annualNet), false],
              ].map(([k, v, sub], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: sub ? "2px 0 2px 12px" : "9px 0", borderTop: (!sub && i > 0) ? "1px solid #F0F0F0" : "none" }}>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: sub ? 11.5 : 14, color: sub ? "#A0A0A0" : "#1A1A1A", fontWeight: !sub && i >= 9 ? 700 : 400 }}>{k}</span>
                  {!sub && <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, color: i >= 9 ? "#4A6DC7" : "#1A1A1A" }}>{v}</span>}
                </div>
              ))}
            </div>

            {/* Sensitivity matrix */}
            <div style={{ ...cardStyle, marginBottom: 20 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", margin: "0 0 4px" }}>Profit sensitivity</p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#999", margin: "0 0 14px" }}>Monthly net at different occupancy & nightly rate combinations.</p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 360 }}>
                  <thead>
                    <tr>
                      <th style={{ padding: "8px 6px", fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left" }}>Occ ↓ / Rate →</th>
                      {rateCols.map((r, i) => <th key={i} style={{ padding: "8px 4px", fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, color: "#666", textAlign: "center" }}>£{r}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {occRows.map((o, ri) => (
                      <tr key={ri}>
                        <td style={{ padding: "6px", fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, color: "#666" }}>{o}%</td>
                        {rateCols.map((r, ci) => {
                          const n = netAt(r, o);
                          const isCurrent = o === occBase && r === rateBase;
                          const bg = n < 0 ? "#FDECEC" : n < monthlyGross * 0.2 ? "#FFF7E6" : "#E6F4EA";
                          const col = n < 0 ? "#B3261E" : "#1E7E34";
                          return (
                            <td key={ci} style={{ padding: "7px 4px", textAlign: "center", fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: isCurrent ? 800 : 600, color: col, background: isCurrent ? "#E8EDFF" : bg, border: isCurrent ? "2px solid #4A6DC7" : "1px solid #fff", borderRadius: 4 }}>{fmt(n)}</td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#999", margin: "0 0 20px", lineHeight: 1.6, fontStyle: "italic" }}>Estimates based on Truestays managed-portfolio data and UK market benchmarks. Not a guarantee of earnings. Your actual income depends on property condition, presentation, dynamic pricing and demand.</p>
          </div>

        </div>
      </div>

      {/* SEO content */}
      <div style={{ maxWidth: 760, margin: "64px auto 0", fontFamily: "'DM Sans', sans-serif" }}>
        <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(22px, 2.5vw, 30px)", color: "#1A1A1A", letterSpacing: "-0.02em", marginBottom: 16 }}>How the Airbnb deal analyser works</h2>
        <p style={{ fontSize: 15, color: "#555", lineHeight: 1.7, margin: "0 0 16px" }}>The Truestays Airbnb Income Analyser turns a few property details into a complete, transparent short-let profit forecast. Pick your city, bedrooms and property type and the tool pre-fills a typical nightly rate and occupancy from our managed portfolio across 16 UK markets. Every revenue, cost and fee line is then editable, so you can model your exact situation — from a mortgage-free studio in Manchester to a four-bed house in Brighton.</p>
        <p style={{ fontSize: 15, color: "#555", lineHeight: 1.7, margin: "0 0 24px" }}>Unlike a simple earnings calculator, the analyser builds a full profit &amp; loss view: gross revenue, fixed operating costs, variable changeover costs, platform commissions, the management fee with VAT, and payment processing — then projects a seasonally-adjusted 12-month net profit chart and a sensitivity matrix showing how your income holds up at different occupancy and nightly-rate combinations. Export the whole thing as a PDF, no email required.</p>

        <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(20px, 2.2vw, 26px)", color: "#1A1A1A", letterSpacing: "-0.02em", margin: "32px 0 14px" }}>What's included in the analysis</h2>
        <ul style={{ fontSize: 15, color: "#555", lineHeight: 1.8, margin: "0 0 8px", paddingLeft: 20 }}>
          <li><strong>Revenue</strong> — nightly rate × occupancy × 30 nights, with city benchmarks pre-filled.</li>
          <li><strong>Fixed costs</strong> — utilities, council tax, internet, insurance and mortgage/finance.</li>
          <li><strong>Variable costs</strong> — cleaning per changeover (driven by average length of stay) plus a sundries percentage.</li>
          <li><strong>Fees &amp; commission</strong> — blended platform/OTA commission, management fee, VAT on the management fee, and payment processing.</li>
          <li><strong>12-month projection</strong> — seasonal demand applied to your average monthly net.</li>
          <li><strong>Sensitivity matrix</strong> — monthly net across five occupancy and five nightly-rate scenarios.</li>
          <li><strong>PDF export</strong> — a clean, branded summary to share or keep on file.</li>
        </ul>

        <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(20px, 2.2vw, 26px)", color: "#1A1A1A", letterSpacing: "-0.02em", margin: "32px 0 16px" }}>Frequently asked questions</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {FAQS.map((f, i) => (
            <div key={i}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15.5, color: "#1A1A1A", margin: "0 0 6px", display: "flex", alignItems: "flex-start", gap: 8 }}><Info size={16} color="#4A6DC7" style={{ marginTop: 3, flexShrink: 0 }} /> {f.q}</p>
              <p style={{ fontSize: 14.5, color: "#666", lineHeight: 1.7, margin: 0, paddingLeft: 24 }}>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 980px) { .analyser-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 560px) { .analyser-metrics { grid-template-columns: 1fr 1fr !important; } }
      `}</style>
    </ToolShell>
  );
}